/**
 * F-0530 (AC-19.7, `docs/concept-mobile-app.md`): a work area's badge — its initials on a colour —
 * drawn beside its name wherever the phone names the app's scope: the top row's title, the native
 * switcher sheet and the More drawer's row. One function, so the three can never disagree.
 *
 * ⭐ **The colour comes from the id, never the name.** A rename keeps the colour, which is what makes
 * the badge recognisable at a glance; a hash of the name would repaint the work area on every rename.
 *
 * The fills are `Avatar.tsx`'s `AVATAR_FILLS` as hex, so the native shell can draw them too: each one
 * holds white initials at 4.5:1 or more (I-0890).
 */

/** Tailwind's blue-600, emerald-700, amber-700, rose-600, violet-600, cyan-700, pink-600, indigo-600. */
export const WORK_AREA_BADGE_COLORS = [
  '#2563eb',
  '#047857',
  '#b45309',
  '#e11d48',
  '#7c3aed',
  '#0e7490',
  '#db2777',
  '#4f46e5',
] as const;

export interface IWorkAreaBadge {
  /** One or two capital letters; `?` for a name with no letter or digit in it. */
  readonly initials: string;
  /** A `#rrggbb` fill from {@link WORK_AREA_BADGE_COLORS}. */
  readonly color: string;
}

function hashOf(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

/** The first letter of the first and last words, `Phone UX review` → `PR`; one word → its first. */
export function workAreaInitialsOf(name: string): string {
  const words = name
    .trim()
    .split(/[\s\-_/.]+/)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word !== '');
  if (words.length === 0) return '?';
  const first = [...words[0]][0];
  if (words.length === 1) return first.toUpperCase();
  return (first + [...words[words.length - 1]][0]).toUpperCase();
}

export function workAreaBadgeOf(workArea: {
  readonly id: string;
  readonly name: string;
}): IWorkAreaBadge {
  return {
    initials: workAreaInitialsOf(workArea.name),
    color: WORK_AREA_BADGE_COLORS[hashOf(workArea.id) % WORK_AREA_BADGE_COLORS.length],
  };
}
